import { useFieldArray, useForm } from "react-hook-form";
import { CategoryModal } from "../CategoryGrid";
import * as React from "react";
import { employeeCommutingTransport, flattenDimensionItems, renderDimensionFormCells, renderOptions } from "../Category/helpers";
import { useDimensionFiltering } from "../Category/useDimensionFiltering";
import { FormSelect, NumericFormInput } from "../../../../../../components/Forms";
import { useTranslation } from "react-i18next";
import { VALIDATION_PATTERNS } from "../../../../../../utils";

function EmployeeCommutingModalWrapper({ tabKey, modalData, attributesOrder, isOpen, onClose, onSubmit, getEmptyRow }) {
  const { fieldArrayName, editData, organizationStructure, dimensionItems } = modalData;
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const seededEditData = editData ? flattenDimensionItems(editData) : null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch
  } = useForm({
    defaultValues: {
      [fieldArrayName]: [seededEditData ? seededEditData : getEmptyRow()]
    }
  });

  const { fields, append, remove, update: updateItem } = useFieldArray({
    control,
    name: fieldArrayName
  });

  const watchFieldArray = watch(fieldArrayName);

  const { getFilteredItems, setChangedRowIndex } = useDimensionFiltering({
    dimensionItems,
    editData: seededEditData,
    watchFieldArray
  });

  const mapRow = (row, index) => {
    const gridItems = [];

    // ESG-3894: prepend dimension dropdowns
    gridItems.push(...renderDimensionFormCells({
      organizationStructure,
      dimensionItems,
      register,
      fieldArrayName,
      index,
      watchFieldArray,
      getFilteredItems,
      onDimensionChange: setChangedRowIndex
    }));

    const modeOfTransportSelector = register(`${fieldArrayName}.${index}.modeOfTransport`, {
      required: globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.modeOfTransport`) })
    });

    gridItems.push(
      <FormSelect
        errors={errors}
        {...modeOfTransportSelector}
        onChange={(e) => {
          modeOfTransportSelector.onChange(e);
          updateItem(index, { ...watchFieldArray[index], transportSubtype: "" });
        }}
        placeholder=" "
        title={watchFieldArray[index].modeOfTransport !== "" ?
          t(`inputs.modeOfTransportOptions.${watchFieldArray[index].modeOfTransport}`) : ""}
      >
        {renderOptions(employeeCommutingTransport.map(el => el.mode),
          "organizationQuestionnaire.scope3.inputs.modeOfTransportOptions")}
      </FormSelect>
    );

    gridItems.push(
      <FormSelect
        errors={errors}
        {...register(`${fieldArrayName}.${index}.transportSubtype`, {
          required: watchFieldArray[index].modeOfTransport !== "walking" && watchFieldArray[index].modeOfTransport !== "cycling" ?
            globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.transportSubtype`) }) : false
        })}
        placeholder=" "
        title={watchFieldArray[index].transportSubtype !== "" ?
          t(`inputs.transportSubtypeOptions.${watchFieldArray[index].modeOfTransport}.${watchFieldArray[index].transportSubtype}`) : ""}
      >
        {renderOptions(employeeCommutingTransport.find(el => el.mode === watchFieldArray[index].modeOfTransport)?.subtypes ?? [],
          `organizationQuestionnaire.scope3.inputs.transportSubtypeOptions.${watchFieldArray[index].modeOfTransport}`)}
      </FormSelect>
    );

    gridItems.push(
      <NumericFormInput
        errors={errors}
        type="number"
        {...register(`${fieldArrayName}.${index}.commutingDistance`, {
          required: globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.commutingDistance`) }),
          pattern: VALIDATION_PATTERNS.POSITIVE_NUMBER
        })}
      />
    );

    gridItems.push(
      <NumericFormInput
        errors={errors}
        type="number"
        {...register(`${fieldArrayName}.${index}.daysWorkedInOfficePerYear`, {
          required: globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.daysWorkedInOfficePerYear`) }),
          pattern: VALIDATION_PATTERNS.DAYS_PER_YEAR
        })}
      />
    );

    gridItems.push(
      <NumericFormInput
        errors={errors}
        type="number"
        {...register(`${fieldArrayName}.${index}.numberOfEmployees`, {
          required: globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.numberOfEmployees`) }),
          pattern: VALIDATION_PATTERNS.POSITIVE_INTEGER
        })}
      />
    );

    return gridItems;
  };

  return (
    <CategoryModal
      modalData={modalData}
      formData={{ handleSubmit, fields, append, remove }}
      attributesOrder={attributesOrder}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      getEmptyRow={getEmptyRow}
      mapRow={mapRow}
    />
  );

}

export default EmployeeCommutingModalWrapper;